'use client'

import { useDashboardData } from '@/hooks/useDashboardData'
import { useFilteredData } from '@/hooks/useFilters'
import KPICard from '@/components/ui/KPICard'
import ThroughputChart from '@/components/charts/ThroughputChart'
import WIPChart from '@/components/charts/WIPChart'
import LeadCycleChart from '@/components/charts/LeadCycleChart'
import FlowEffChart from '@/components/charts/FlowEffChart'

export default function DashboardPage() {
  const { isLoading } = useDashboardData()
  const { kpis, periodDone, filteredTasks, filteredTS, periodTS, filterParams } = useFilteredData()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        Loading dashboard...
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <KPICard value={kpis.total} label="Total Tasks" tooltip="All non-cancelled tasks" />
        <KPICard value={kpis.inProg} label="In Progress" />
        <KPICard value={kpis.completed} label="Completed" sublabel={`last ${filterParams.period}d`} />
        <KPICard value={`${kpis.avgLead.toFixed(1)}d`} label="Avg Lead Time" tooltip="Business days from creation to done" />
        <KPICard value={`${kpis.avgCycle.toFixed(1)}d`} label="Avg Cycle Time" tooltip="Business days from assignment to done" />
        <KPICard
          value={`${kpis.avgFlow.toFixed(0)}%`}
          label="Flow Efficiency"
          status={kpis.avgFlow < 15 ? 'bad' : kpis.avgFlow < 30 ? 'warning' : 'good'}
          tooltip="Active work hours / total calendar time"
        />
        <KPICard value={`${kpis.totalH.toFixed(0)}h`} label="Total Hours" />
        <KPICard
          value={`${kpis.billPct.toFixed(0)}%`}
          label="Billable Util."
          status={kpis.billPct > 60 ? 'good' : kpis.billPct > 40 ? 'warning' : 'bad'}
          tooltip="(MSO+PSO) / total hours"
        />
        <KPICard
          value={`${kpis.meetPct.toFixed(0)}%`}
          label="Meeting Ratio"
          status={kpis.meetPct > 40 ? 'bad' : kpis.meetPct > 25 ? 'warning' : 'good'}
          sublabel={`${kpis.meetH.toFixed(0)}h meetings`}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <h3 className="text-base font-semibold mb-4">Throughput</h3>
          <ThroughputChart tasks={filteredTasks} />
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <h3 className="text-base font-semibold mb-4">WIP Over Time</h3>
          <WIPChart tasks={filteredTasks} />
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <h3 className="text-base font-semibold mb-4">Lead & Cycle Time Distribution</h3>
          <LeadCycleChart leads={kpis.leads} cycles={kpis.cycles} />
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <h3 className="text-base font-semibold mb-4">Flow Efficiency</h3>
          <FlowEffChart tasks={periodDone} />
        </div>
      </div>
    </div>
  )
}
